"use client"

import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Clock, Check, Minus, List } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useAvailableServices } from "@/hooks/modal/useAvailableServices"
import { useComputedPayment } from "@/hooks/modal/useComputedPayment"
import { ServiceStatusPanel } from "./service-status-panel"
// Centralized cost calculation engine
import {
  getServiceLineTotal,
  getServiceUnitPrice,
  createServiceFromCatalog,
  applyManualPriceChange,
  applyDiscountChange,
} from "@/lib/appointment/costEngine"


function formatDuration(seconds?: number) {
  if (!seconds || seconds <= 0) return "—"
  const m = Math.floor(seconds / 60)
  const h = Math.floor(m / 60)
  const mm = m % 60
  return h ? `${h} ч ${mm} м` : `${mm} м`
}

// HH:mm + seconds -> HH:mm (wraps at 24h)
function addSecondsToTime(time: string, seconds: number) {
  const [h, m] = time.split(":").map(Number)
  const totalMinutes = h * 60 + m + Math.floor(seconds / 60)
  const newH = Math.floor(totalMinutes / 60) % 24
  const newM = totalMinutes % 60
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`
}

export function StatusSectionMobile() {
  const { t } = useTranslation()
  const { appointment, updateAppointment } = useAppointmentModal()
  const payment = useComputedPayment()

  // Attendance status
  const setAttendance = (val: -1 | 0 | 1) => updateAppointment({ attendance: val })
  const activeAttendance = (appointment?.attendance ?? 0) as -1 | 0 | 1

  // Auto-duration toggle (default true to preserve desktop behaviour)
  const [autoDuration, setAutoDuration] = useState<boolean>(true)

  // Service picker (mirror desktop StatusSection)
  const masterId = appointment?.staff?.id ? Number(appointment.staff.id) : undefined
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false)
  const { data: availableServices, isLoading: isLoadingServices, error: servicesError } =
    useAvailableServices(masterId)

  // Total service length in seconds (sum length * amount)
  const totalLengthSec = useMemo(() => {
    const list = appointment?.services || []
    return list.reduce((sum: number, s: any) => sum + ((s.length || 0) * (s.amount || 1)), 0)
  }, [appointment?.services])

  // Recalculate endTime if autoDuration is enabled
  const handleRecalcEndTime = (servicesAfterChange: any[]) => {
    if (!appointment) return
    // update services always
    if (!autoDuration || !appointment.startTime) {
      updateAppointment({ services: servicesAfterChange })
      return
    }
    const total = servicesAfterChange.reduce((sum, s) => sum + ((s.length || 0) * (s.amount || 1)), 0)
    const newEnd = total > 0 ? addSecondsToTime(appointment.startTime, total) : appointment.endTime
    updateAppointment({ services: servicesAfterChange, endTime: newEnd })
  }

  // Add service from picker - use centralized createServiceFromCatalog
  const handleAddService = (serviceId: number) => {
    if (!availableServices || !appointment) return
    const selected = availableServices.find((s: any) => s.id === serviceId)
    if (!selected) return

    // Use centralized service creation from costEngine
    const newService = createServiceFromCatalog(selected)

    const servicesAfter = Array.isArray(appointment.services)
      ? [...appointment.services, newService]
      : [newService]

    handleRecalcEndTime(servicesAfter)
    setServiceDialogOpen(false)
  }

  // Update/remove helpers (by index)
  const updateServiceAt = (idx: number, patch: any) => {
    if (!appointment?.services) return
    const next = appointment.services.map((s: any, i: number) => (i === idx ? { ...s, ...patch } : s))
    handleRecalcEndTime(next)
  }
  const removeServiceAt = (idx: number) => {
    if (!appointment?.services) return
    // An appointment must keep at least one service — saving an empty list
    // strips every yrecordservice row and orphans the record. The backend
    // rejects it too; this stops the admin reaching a save that can only fail.
    // Removing the appointment itself is Delete, not an empty service list.
    if (appointment.services.length <= 1) return
    const next = appointment.services.filter((_, i: number) => i !== idx)
    handleRecalcEndTime(next)
  }

  // Line total calculation - use centralized engine
  // computeToPay has been replaced with getServiceLineTotal from costEngine

  if (!appointment) {
    return <div className="flex-1 p-6 text-sm text-muted-foreground">{t("appointment.status.loading", "Loading appointment data...")}</div>
  }

  return (
    <div className="space-y-4">
      {/* Status controls — icon-only, styled by global tokens to match dev */}
      <div className="status-icon-row">
        <button
          type="button"
          className="status-icon-btn wait"
          data-active={activeAttendance === 0}
          aria-pressed={activeAttendance === 0}
          aria-label={t("appointment.status.waiting", "Waiting")}
          title={t("appointment.status.waiting_for_client", "Waiting for client")}
          onClick={() => setAttendance(0)}
        >
          <Clock />
          <span className="sr-only">{t("appointment.status.waiting", "Waiting")}</span>
        </button>
        <button
          type="button"
          className="status-icon-btn arrived"
          data-active={activeAttendance === 1}
          aria-pressed={activeAttendance === 1}
          aria-label={t("appointment.status.arrived", "Arrived")}
          title={t("appointment.status.arrived", "Arrived")}
          onClick={() => setAttendance(1)}
        >
          <Check />
          <span className="sr-only">{t("appointment.status.arrived", "Arrived")}</span>
        </button>
        <button
          type="button"
          className="status-icon-btn noshow"
          data-active={activeAttendance === -1}
          aria-pressed={activeAttendance === -1}
          aria-label={t("appointment.status.no_show", "No show")}
          title={t("appointment.status.no_show", "No show")}
          onClick={() => setAttendance(-1)}
        >
          <Minus />
          <span className="sr-only">{t("appointment.status.no_show", "No show")}</span>
        </button>
        <button
          type="button"
          className="status-icon-btn confirmed"
          data-active={appointment?.confirmed === 1}
          aria-pressed={appointment?.confirmed === 1}
          aria-label={t("appointment.status.confirmed", "Confirmed")}
          title={t("appointment.status.confirmed", "Confirmed")}
          onClick={() => updateAppointment({ confirmed: appointment?.confirmed === 1 ? 0 : 1 })}
        >
          <Check />
          <span className="sr-only">{t("appointment.status.confirmed", "Confirmed")}</span>
        </button>
      </div>

      {/* Add service row */}
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" className="h-8 px-2 text-xs" onClick={() => setServiceDialogOpen(true)}>
          <List className="w-3.5 h-3.5 mr-1" /> {t("appointment.status.select_from_list", "Select from list")}
        </Button>
        <div className="text-xs text-muted-foreground">{t("appointment.status.total_duration", "Total duration:")} {formatDuration(totalLengthSec)}</div>
      </div>

      {/* Service picker dialog */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("appointment.status.select_service", "Select service")}</DialogTitle>
            <DialogDescription>{t("appointment.status.available_services_list", "List of available services to add to appointment")}</DialogDescription>
          </DialogHeader>
          {isLoadingServices && <div className="text-xs text-gray-500 py-4">{t("appointment.status.loading_services", "Loading services...")}</div>}
          {servicesError && <div className="text-xs text-red-500 py-4">{t("appointment.status.error_loading_services", "Error loading services")}</div>}
          {!isLoadingServices && !servicesError && (
            <div className="max-h-64 overflow-y-auto overscroll-contain touch-pan-y">
              {Array.isArray(availableServices) && availableServices.length > 0 ? (
                <ul>
                  {availableServices.map((service: any) => (
                    <li key={service.id}>
                      <Button variant="ghost" className="w-full justify-between text-xs" onClick={() => handleAddService(service.id)}>
                        <span>{service.title}</span>
                        <span className="text-gray-500 ml-2">{formatDuration(service.length)}</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-500 py-4">{t("appointment.status.no_available_services", "No available services")}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Services rendered as panels (mobile) */}
      <div className="space-y-3">
        {Array.isArray(appointment.services) && appointment.services.length > 0 ? (
          appointment.services.map((s: any, idx: number) => {
            const qty = Math.max(1, s.amount ?? 1)
            // Use centralized getServiceUnitPrice
            const price = getServiceUnitPrice(s)
            const disc = Math.max(0, s.discount ?? 0)
            // Use centralized getServiceLineTotal
            const toPay = getServiceLineTotal(s)
            const specialist = appointment?.staff?.name || ""
            return (
              <ServiceStatusPanel
                key={`${s.id}-${idx}`}
                serviceName={s.title}
                specialist={specialist}
                quantity={qty}
                price={price}
                discount={disc}
                toPay={toPay}
                onQuantityChange={undefined}
                onPriceChange={(newPrice) => {
                  // Use centralized applyManualPriceChange
                  const costPatch = applyManualPriceChange(s as any, newPrice * qty);
                  updateServiceAt(idx, costPatch);
                }}
                onDiscountChange={(newDisc) => {
                  // Use centralized applyDiscountChange
                  const costPatch = applyDiscountChange(s as any, newDisc);
                  updateServiceAt(idx, costPatch);
                }}
                onDelete={
                  appointment.services.length > 1 ? () => removeServiceAt(idx) : undefined
                }
              />
            )
          })
        ) : (
          <div className="text-sm text-muted-foreground">{t("appointment.status.no_services_added", "No services added")}</div>
        )}
      </div>

      {/* Notes - mirror desktop */}
      <div>
        <div className="text-xs mb-2">{t("appointment.status.note", "Note")}</div>
        <textarea
          className="w-full border border-gray-300 rounded-md p-2 h-24 text-xs"
          placeholder={t("appointment.status.add_comment", "Add a comment to the appointment")}
          value={appointment.comment || ""}
          onChange={(e) => updateAppointment({ comment: e.target.value })}
        />
      </div>

      {/* Auto-duration toggle */}
      <div className="flex items-center justify-between">
        <div className="text-sm">{t("appointment.status.auto_duration", "Auto duration")}</div>
        <Switch checked={autoDuration} onCheckedChange={setAutoDuration} />
      </div>

      {/* Visit time summary */}
      {appointment?.startTime && appointment?.endTime && (
        <div className="text-xs text-muted-foreground">
          {t("appointment.status.visit_time", "Visit time:")} {appointment.startTime} – {appointment.endTime}
        </div>
      )}

      {/* Total (readonly) */}
      <div>
        <div className="text-xs text-gray-500 mb-1">{t("appointment.status.total_to_charge", "Total to charge")}</div>
        <div className="text-xl font-medium">{payment?.totalAmountString ?? ""}</div>
      </div>
    </div>
  )
}
